import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Upload, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { extractError } from "@/api/client";

/**
 * Reusable CSV bulk import dialog.
 * - entity: "users" | "contacts"
 * - columns: list of {key, label, required?, help?}
 * - endpoint: e.g. "/users/bulk"
 */
export default function BulkImportDialog({ open, onClose, entity, title, columns, endpoint, onDone }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const parseCSV = (text) => {
    const clean = text.replace(/^\ufeff/, "").trim();
    const lines = clean.split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return [];
    const delim = lines[0].includes(";") ? ";" : ",";
    const parseLine = (line) => {
      // Simple RFC4180 parser
      const out = []; let cur = ""; let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') { inQuotes = false; }
          else cur += ch;
        } else {
          if (ch === '"') inQuotes = true;
          else if (ch === delim) { out.push(cur); cur = ""; }
          else cur += ch;
        }
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = parseLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
    return lines.slice(1).map((l) => {
      const values = parseLine(l);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = values[i] ?? ""; });
      return obj;
    });
  };

  const handleFile = (f) => {
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (e) => setRows(parseCSV(e.target.result || ""));
    reader.readAsText(f, "utf-8");
  };

  const downloadTemplate = () => {
    const header = columns.map((c) => c.key).join(";") + "\n";
    const example = columns.map((c) => c.example || "").join(";") + "\n";
    const blob = new Blob(["\ufeff" + header + example], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `template-${entity}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (rows.length === 0) return toast.error("Aucune ligne à importer");
    setRunning(true);
    try {
      const { data } = await api.post(endpoint, { items: rows });
      setResult(data);
      toast.success(`${data.created} ligne(s) créée(s)`);
      onDone?.();
    } catch (e) {
      toast.error(extractError(e, "Échec de l'import"));
    } finally { setRunning(false); }
  };

  const close = () => { setRows([]); setFileName(""); setResult(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import CSV — {title || (entity === "users" ? "Utilisateurs" : "Contacts")}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
            <div className="font-semibold text-slate-700 mb-1">Format attendu</div>
            <div className="text-slate-600">Séparateur : <code>;</code> ou <code>,</code>. Première ligne = entêtes.</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {columns.map((c) => (
                <span key={c.key} className={`px-1.5 py-0.5 rounded ${c.required ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-700"}`}>{c.key}{c.required ? " *" : ""}</span>
              ))}
            </div>
            <Button data-testid="download-template" variant="outline" size="sm" onClick={downloadTemplate} className="mt-3">
              <Download size={12} className="mr-1.5" /> Télécharger un template
            </Button>
          </div>

          <div>
            <Label>Fichier CSV</Label>
            <input
              type="file" accept=".csv,text/csv" data-testid="bulk-file"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="text-sm block mt-1 w-full"
            />
            {fileName && <div className="text-xs text-slate-500 mt-1">{fileName} — {rows.length} ligne(s) détectée(s)</div>}
          </div>

          {rows.length > 0 && !result && (
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Aperçu (5 premières lignes)</div>
              <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50">{columns.map((c) => <th key={c.key} className="text-left px-2 py-1.5 font-semibold text-slate-600 whitespace-nowrap">{c.label}</th>)}</tr></thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        {columns.map((c) => <td key={c.key} className="px-2 py-1 text-slate-700 whitespace-nowrap max-w-[180px] truncate">{r[c.key] || "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs">
              <div className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={14} /> Résultat</div>
              <div className="mt-1 text-emerald-700">Créés : {result.created}{result.skipped !== undefined ? ` • Ignorés : ${result.skipped}` : ""}</div>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <div className="font-semibold text-red-700 flex items-center gap-1.5"><AlertCircle size={14} /> {result.errors.length} erreur(s)</div>
                  <ul className="mt-1 list-disc list-inside text-red-700">
                    {result.errors.slice(0, 5).map((e, i) => <li key={i}>Ligne {e.row} : {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Fermer</Button>
          {!result && (
            <Button data-testid="bulk-submit" onClick={submit} disabled={rows.length === 0 || running} className="text-white" style={{ background: "#023E73" }}>
              <Upload size={14} className="mr-1.5" /> {running ? "Import..." : `Importer ${rows.length} ligne(s)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
